import type { FilterOptions, Order, Supplier } from '~/types'

interface FrequentOrderItem {
  name: string
  handle: string
  supplier: Supplier
  lastOrderedQuantity: number
  totalOrderQuantity: number
  loading?: boolean
}

interface OrderHistoryResponse {
  totalOrders: number
  orders: Order[]
  filterOptions: FilterOptions
}

export type OrderRequestType = 'invoice' | 'orderStatus'

export const useOrderHistoryStore = defineStore('order-history', () => {
  const { baseURL } = useRuntimeConfig().app
  const { $i18n, $storeErrorLog } = useNuxtApp()
  const userStore = useUserStore()
  const cc = useConstants()

  const itemsPerPage = computed(() => 10)
  const page = ref(1)
  const list = ref<any[]>([])
  const totalOrders = ref(0)
  const filterOptions = ref<FilterOptions>({})
  const filter = ref<Record<string, string[]>>({})
  const query = computed(() => ({
    ...Object.fromEntries(
      Object.entries(filter.value)
        .map(([key, value]) => [cc.QUERY_PARAMS[key as keyof typeof cc.QUERY_PARAMS] ?? key, value]),
    ),
    p: page.value,
  }))

  const {
    data: ordersData,
    pending: isLoading,
    refresh: reloadOrders,
  } = useFetch<OrderHistoryResponse>('/api/v2/orders', {
    baseURL,
    query,
    onResponse: ({ response }) => {
      // this method is also used to load more orders, that's why we need to merge the results
      list.value.push(...response._data.orders)
      totalOrders.value = totalOrders.value || response._data.totalOrders
      filterOptions.value = Object.keys(filterOptions.value).length
        ? filterOptions.value
        : response._data.filterOptions
    },
  })

  const totalPages = computed(() => Math.ceil(totalOrders.value / itemsPerPage.value))

  const customerHandle = computed(() => userStore?.customerHandle ?? undefined)
  const {
    data: frequentOrdersData,
    refresh: reloadFrequentOrders,
  } = useFetch<{ items: FrequentOrderItem[] }>(
    wp(
      '/api/easyApi/customers/:handle/order-history/frequent',
      { handle: customerHandle.value ?? '' },
    ),
    { baseURL },
  )

  const $reset = async () => {
    list.value = []
    filter.value = {}
    page.value = 1
    totalOrders.value = 0
    filterOptions.value = {}
    frequentOrdersData.value = null
    ordersData.value = null
  }

  const nextPage = () => page.value++

  const startPage = () => page.value = 1

  const toggleFilterValue = (key: string, value: string) => {
    list.value = []
    page.value = 1
    const active = filter.value[key]?.includes(value)
    if (active)
      filter.value[key] = filter.value[key].filter(v => v !== value)
    else
      filter.value[key] = [...(filter.value[key] ?? []), value]
  }

  const clearFilters = (...keys: string[]) => {
    list.value = []
    page.value = 1
    if (keys.length)
      keys.forEach(key => filter.value[key] = [])
    else
      filter.value = {}
  }

  const postOrderRequest = async (
    requestType: OrderRequestType,
    handle: string,
  ) => {
    const urls: Record<OrderRequestType, string> = {
      invoice: `/api/v2/orders/${handle}/request-invoice`,
      orderStatus: `/api/v2/orders/${handle}/request-order-status`,
    }
    const url = urls[requestType]
    const [err] = await attempt($fetch(url, { method: 'POST', baseURL }))
    if (err) {
      $storeErrorLog(
        'stores/order-history.ts',
        `Could not post request for ${requestType}`,
        $i18n.t('errors.page.unknown.subtitle'),
        err,
      )
    }
  }

  return {
    $reset,
    itemsPerPage,
    page,
    ordersData,
    frequentOrdersData,
    filter,
    list,
    isLoading,
    totalOrders,
    totalPages,
    filterOptions,
    reloadOrders,
    reloadFrequentOrders,
    nextPage,
    startPage,
    toggleFilterValue,
    clearFilters,
    postOrderRequest,
  }
})
